import React, { useState, useEffect } from 'react'
import { Check, X, Minus, Clock } from 'lucide-react'
import { FeaturedLayout } from '../layouts'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useAuth } from '../../hooks/useAuth'
import {
  getAllSubscriptionPlansWithPrices,
  getCurrentSubscription,
  getPendingRequestForOrg,
  createPlanChangeRequest,
  cancelPlanChangeRequest,
} from '../../lib/supabase'
import PlanBadge from '../ui/PlanBadge'
import { cn } from '../../lib/utils'

// Las filas de features agrupadas por categoria.
// type 'limit' -> muestra el valor numerico (limits[key])
// type 'flag'  -> muestra check/x segun features[key]
// type 'always'-> siempre check (es del nucleo, no se bloquea)
const FEATURE_GROUPS = [
  {
    title: 'Limites',
    items: [
      { key: 'players_max', label: 'Jugadores activos', type: 'limit' },
      { key: 'groups_max', label: 'Grupos', type: 'limit' },
      { key: 'staff_max', label: 'Staff', type: 'limit' },
      { key: 'deportes_max', label: 'Deportes activos', type: 'limit' },
    ],
  },
  {
    title: 'Operacion del equipo',
    items: [
      { label: 'Asistencia (entrenamientos y juegos)', type: 'always' },
      { label: 'Agenda y calendario', type: 'always' },
      { label: 'Horarios de entrenamiento recurrentes', type: 'always' },
      { label: 'Tarjeton e impresion', type: 'always' },
      { key: 'categories_custom', label: 'Categorias personalizadas', type: 'flag' },
    ],
  },
  {
    title: 'Torneos',
    items: [
      { label: 'Inscripcion a torneos propios', type: 'always' },
      { label: 'Roster oficial y staff por torneo', type: 'always' },
      { label: 'Marcador, eventos y posiciones', type: 'always' },
      { key: 'cross_org_inscription', label: 'Inscripcion a torneos cross-org', type: 'flag' },
    ],
  },
  {
    title: 'Programas de entrenamiento',
    items: [
      { key: 'programs', label: 'Biblioteca de programas y planes', type: 'flag' },
      { key: 'programs', label: 'Asignacion a grupos y fechas', type: 'flag' },
    ],
  },
  {
    title: 'Cobranza',
    items: [
      { key: 'payments_module', label: 'Conceptos, planes y promociones', type: 'flag' },
      { key: 'payments_module', label: 'Familias y descuento por hermano', type: 'flag' },
      { key: 'payments_module', label: 'Estado de cuenta + WhatsApp publico', type: 'flag' },
      { key: 'payments_module', label: 'Saldo a favor y anulacion de pagos', type: 'flag' },
    ],
  },
  {
    title: 'Uniformes',
    items: [
      { key: 'uniforms_module', label: 'Catalogo de prendas', type: 'flag' },
      { key: 'uniforms_module', label: 'Entregas y compras extras', type: 'flag' },
    ],
  },
  {
    title: 'Inventario',
    items: [
      { key: 'inventory_module', label: 'Catalogo de articulos y existencias', type: 'flag' },
      { key: 'inventory_module', label: 'Prestamos, conteos y reporte por temporada', type: 'flag' },
    ],
  },
  {
    title: 'Branding',
    items: [
      { key: 'branding_custom', label: 'Logo y colores personalizados', type: 'flag' },
    ],
  },
  {
    title: 'Auditoria y exportacion',
    items: [
      { key: 'audit_log_visible', label: 'Audit log visible', type: 'flag' },
      { key: 'audit_log_days', label: 'Dias de retencion del audit log', type: 'limit', unit: ' dias' },
      { key: 'export_excel', label: 'Exportar a Excel', type: 'flag' },
      { key: 'export_pdf', label: 'Exportar a PDF', type: 'flag' },
    ],
  },
]

const UNLIMITED_THRESHOLD = 5000

const formatLimit = (value, unit = '') => {
  if (value === undefined || value === null) return '—'
  if (typeof value === 'number' && value >= UNLIMITED_THRESHOLD) return 'Ilimitado'
  if (value === 0) return '—'
  return `${value}${unit}`
}

const formatPrice = (amount, currency) => {
  if (amount === 0) return 'Gratis'
  const cents = amount % 100
  const whole = Math.floor(amount / 100)
  const formatted = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(whole + cents / 100)
  return formatted
}

const Cell = ({ value, type, unit, isCurrent }) => {
  const base = 'text-center py-3 px-4 text-sm'
  const ringCls = isCurrent ? 'bg-zinc-50' : ''

  if (type === 'always') {
    return (
      <td className={cn(base, ringCls)}>
        <Check className="size-4 text-emerald-600 mx-auto" />
      </td>
    )
  }

  if (type === 'flag') {
    return (
      <td className={cn(base, ringCls)}>
        {value === true ? (
          <Check className="size-4 text-emerald-600 mx-auto" />
        ) : (
          <X className="size-4 text-zinc-300 mx-auto" />
        )}
      </td>
    )
  }

  if (type === 'limit') {
    return (
      <td className={cn(base, ringCls, 'font-medium')}>
        {formatLimit(value, unit)}
      </td>
    )
  }

  return <td className={cn(base, ringCls)}><Minus className="size-4 text-zinc-300 mx-auto" /></td>
}

const PlanComparison = () => {
  const { org } = useOrganization()
  const { staff } = useAuth()
  const [plans, setPlans] = useState([])
  const [currentPlanCode, setCurrentPlanCode] = useState('starter')
  const [pendingRequest, setPendingRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [currency, setCurrency] = useState('MXN')
  const [billingCycle, setBillingCycle] = useState('monthly')

  // Modal de solicitud
  const [requestingPlan, setRequestingPlan] = useState(null)
  const [requestNote, setRequestNote] = useState('')
  const [submittingRequest, setSubmittingRequest] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [cancelingRequest, setCancelingRequest] = useState(false)

  const isFounder = staff?.is_founder === true && staff?.org_id === org?.id
  const isSameOrgContext = org?.id && staff?.org_id === org?.id

  const reloadPending = async () => {
    if (!org?.id) return
    const { data } = await getPendingRequestForOrg(org.id)
    setPendingRequest(data || null)
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const [plansRes, subRes, pendingRes] = await Promise.all([
        getAllSubscriptionPlansWithPrices(),
        org?.id ? getCurrentSubscription(org.id) : Promise.resolve({ data: null }),
        org?.id ? getPendingRequestForOrg(org.id) : Promise.resolve({ data: null }),
      ])
      if (cancelled) return
      // Solo los planes de la app de esta org (no mezclar Club con Torneos).
      const appKey = (org?.type === 'league' || org?.type === 'tournament_organizer') ? 'torneos' : 'club'
      if (plansRes.data) setPlans(plansRes.data.filter((p) => (p.app_key || 'club') === appKey))
      if (subRes?.data?.plan?.code) setCurrentPlanCode(subRes.data.plan.code)
      setPendingRequest(pendingRes?.data || null)
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [org?.id])

  const handleOpenRequestModal = (plan) => {
    setRequestingPlan(plan)
    setRequestNote('')
    setSubmitError(null)
  }

  const handleSubmitRequest = async () => {
    if (!requestingPlan || !staff?.id) return
    setSubmittingRequest(true)
    setSubmitError(null)
    const { error } = await createPlanChangeRequest({
      orgId: org.id,
      toPlanCode: requestingPlan.code,
      staffId: staff.id,
      note: requestNote,
    })
    setSubmittingRequest(false)
    if (error) {
      setSubmitError(error.message || 'No se pudo crear la solicitud')
      return
    }
    setRequestingPlan(null)
    setRequestNote('')
    await reloadPending()
  }

  const handleCancelRequest = async () => {
    if (!pendingRequest?.id) return
    setCancelingRequest(true)
    const { error } = await cancelPlanChangeRequest(pendingRequest.id)
    setCancelingRequest(false)
    if (error) return
    setPendingRequest(null)
  }

  const getPriceFor = (plan) => {
    // Starter es siempre gratis independiente de moneda/ciclo
    if (plan.code === 'starter') {
      return { amount: 0, currency, billing_cycle: billingCycle, active: true }
    }
    if (!plan.prices) return null
    return plan.prices.find(
      p => p.active && p.currency === currency && p.billing_cycle === billingCycle
    )
  }

  if (loading) {
    return (
      <FeaturedLayout title="Planes">
        <div className="p-6 text-center text-muted-foreground">Cargando...</div>
      </FeaturedLayout>
    )
  }

  return (
    <FeaturedLayout title="Planes">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-foreground">Compara los planes</h1>
          <p className="text-sm text-muted-foreground">
            Lo que incluye cada plan. Tu plan actual aparece destacado.
            {isFounder && ' Solicita un cambio de plan y MYKLUBB lo aprueba manualmente.'}
          </p>
        </div>

        {pendingRequest && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-3 text-sm text-amber-900">
              <Clock className="size-5 shrink-0" />
              <div>
                <div className="font-semibold">Solicitud pendiente</div>
                <div className="text-xs">
                  Quieres cambiar a <PlanBadge plan={pendingRequest.to_plan?.code} />. MYKLUBB esta revisando.
                </div>
                {pendingRequest.note && (
                  <div className="text-xs mt-1 italic">"{pendingRequest.note}"</div>
                )}
              </div>
            </div>
            {isFounder && (
              <button
                type="button"
                onClick={handleCancelRequest}
                disabled={cancelingRequest}
                className="text-xs font-medium text-amber-900 underline hover:no-underline disabled:opacity-50"
              >
                {cancelingRequest ? 'Cancelando...' : 'Cancelar solicitud'}
              </button>
            )}
          </div>
        )}

        {/* Controles: moneda + ciclo */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-zinc-200 p-0.5 bg-white">
            {['MXN', 'USD'].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCurrency(c)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  currency === c
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-600 hover:text-zinc-900'
                )}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="inline-flex rounded-lg border border-zinc-200 p-0.5 bg-white">
            {[
              { key: 'monthly', label: 'Mensual' },
              { key: 'yearly', label: 'Anual (-17%)' },
            ].map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setBillingCycle(c.key)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  billingCycle === c.key
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-600 hover:text-zinc-900'
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabla comparativa */}
        <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className="text-left p-4 text-xs font-medium text-zinc-500 uppercase tracking-wide w-1/3">
                  Feature
                </th>
                {plans.map((plan) => {
                  const price = getPriceFor(plan)
                  const isCurrent = plan.code === currentPlanCode
                  return (
                    <th
                      key={plan.id}
                      className={cn(
                        'p-4 text-center align-bottom',
                        isCurrent && 'bg-zinc-50'
                      )}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <PlanBadge plan={plan.code} />
                        {price ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xl font-bold text-zinc-900">
                              {formatPrice(price.amount, price.currency)}
                            </span>
                            <span className="text-xs text-zinc-500">
                              {billingCycle === 'monthly' ? '/mes' : '/año'}
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xl font-bold text-zinc-400">—</span>
                            <span className="text-xs text-zinc-400">
                              No disponible
                            </span>
                          </div>
                        )}
                        {isCurrent && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full uppercase tracking-wide">
                            Tu plan actual
                          </span>
                        )}
                        {!isCurrent && isFounder && isSameOrgContext && !pendingRequest && (
                          <button
                            type="button"
                            onClick={() => handleOpenRequestModal(plan)}
                            className="mt-1 text-xs font-medium px-3 py-1.5 rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
                          >
                            Solicitar este plan
                          </button>
                        )}
                        {!isCurrent && pendingRequest?.to_plan?.code === plan.code && (
                          <span className="mt-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                            Solicitud pendiente
                          </span>
                        )}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {FEATURE_GROUPS.map((group) => (
                <React.Fragment key={group.title}>
                  <tr className="bg-zinc-50/50 border-b border-zinc-200">
                    <td
                      colSpan={1 + plans.length}
                      className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-600"
                    >
                      {group.title}
                    </td>
                  </tr>
                  {group.items.map((item, idx) => (
                    <tr
                      key={`${group.title}-${idx}`}
                      className="border-b border-zinc-100 last:border-0"
                    >
                      <td className="p-4 text-sm text-zinc-700">{item.label}</td>
                      {plans.map((plan) => {
                        const isCurrent = plan.code === currentPlanCode
                        let value
                        if (item.type === 'limit') {
                          value = plan.limits?.[item.key]
                        } else if (item.type === 'flag') {
                          value = plan.features?.[item.key] === true
                        }
                        return (
                          <Cell
                            key={`${plan.id}-${idx}`}
                            value={value}
                            type={item.type}
                            unit={item.unit}
                            isCurrent={isCurrent}
                          />
                        )
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground">
          ¿Necesitas algo a la medida o un plan especial? Contacta a soporte de MYKLUBB.
        </p>
      </div>

      {requestingPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-zinc-900">Solicitar cambio de plan</h2>
              <button
                type="button"
                onClick={() => setRequestingPlan(null)}
                disabled={submittingRequest}
                className="p-1 hover:bg-zinc-100 rounded-lg"
              >
                <X size={20} className="text-zinc-500" />
              </button>
            </div>

            <div className="text-sm text-zinc-600">
              Quieres cambiar a <PlanBadge plan={requestingPlan.code} />
            </div>

            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">{submitError}</div>
            )}

            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1">Mensaje opcional para MYKLUBB</label>
              <textarea
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                rows={3}
                placeholder="Ej: Crecimos a 250 jugadores y necesitamos Pro para uniformes."
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-black resize-none"
                maxLength={500}
              />
            </div>

            <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
              MYKLUBB revisara tu solicitud y aplicara el cambio manualmente. Recibiras notificacion en la app cuando este resuelto. Solo el founder puede solicitar cambios.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRequestingPlan(null)}
                disabled={submittingRequest}
                className="px-4 py-2 text-sm font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmitRequest}
                disabled={submittingRequest}
                className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 hover:bg-zinc-800 rounded-lg disabled:opacity-50"
              >
                {submittingRequest ? 'Enviando...' : 'Enviar solicitud'}
              </button>
            </div>
          </div>
        </div>
      )}
    </FeaturedLayout>
  )
}

export default PlanComparison
